import React, { useState } from 'react';
import Link from 'next/link';
import type { TopSearchItemWithDerived, Condition } from '@util/insights/topSearchesNov2025';
import { ConditionAdjuster } from './ConditionAdjuster';

interface DataCardsMobileProps {
  items: TopSearchItemWithDerived[];
  onCompsClick: (item: TopSearchItemWithDerived) => void;
  onListClick: (item: TopSearchItemWithDerived, suggestedAsk: number) => void;
  onConditionAdjust: (skuId: string, condition: Condition) => void;
  onNoteExpand: (skuId: string) => void;
}

export const DataCardsMobile: React.FC<DataCardsMobileProps> = ({
  items,
  onCompsClick,
  onListClick,
  onConditionAdjust,
  onNoteExpand,
}) => {
  const [suggestedAsks, setSuggestedAsks] = useState<Record<string, number>>({});

  const handleConditionChange = (skuId: string, condition: Condition, suggestedPrice: number) => {
    setSuggestedAsks({ ...suggestedAsks, [skuId]: suggestedPrice });
    onConditionAdjust(skuId, condition);
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      Camera: 'bg-blue-600 text-white',
      Lens: 'bg-green-600 text-white',
      Drone: 'bg-purple-600 text-white',
      Accessory: 'bg-orange-600 text-white',
      Lighting: 'bg-yellow-600 text-white',
      Video: 'bg-red-600 text-white',
    };
    return colors[category] || 'bg-gray-600 text-white';
  };

  if (items.length === 0) {
    return (
      <div className="block md:hidden text-center py-12 text-gray-500">
        No items match your filters.
      </div>
    );
  }

  return (
    <div className="block md:hidden space-y-4">
      {items.map((item) => (
        <div
          key={item.sku_id}
          className="bg-white border border-gray-200 rounded-lg p-4 shadow-xs"
        >
          {/* Header: Brand + Model */}
          <div className="mb-3">
            <div className="flex items-center justify-end mb-2">
              <span
                className={`inline-block px-2 py-1 text-xs font-medium rounded ${getCategoryColor(
                  item.category
                )}`}
              >
                {item.category}
              </span>
            </div>
            <h3 className="text-2xl font-bold text-gray-900">
              {item.brand} {item.model}
            </h3>
          </div>

          {/* Price Band */}
          <div className="mb-4">
            <span className="text-xs text-gray-500 block mb-1">Price Range</span>
            <div className="text-2xl font-bold text-gray-900">
              ${item.low_price.toLocaleString()} – ${item.high_price.toLocaleString()}
            </div>
            <div className="text-sm text-gray-600 mt-1">
              Median: ${item.median_price.toLocaleString()}
            </div>
          </div>

          {/* Condition Adjuster */}
          <div className="mb-4 pb-4 border-b border-gray-100">
            <span className="text-xs text-gray-500 block mb-2">Your Condition</span>
            <ConditionAdjuster
              lowPrice={item.low_price}
              highPrice={item.high_price}
              skuId={item.sku_id}
              onConditionChange={(condition, suggestedPrice) =>
                handleConditionChange(item.sku_id, condition, suggestedPrice)
              }
            />
          </div>

          {/* Notes */}
          <div className="mb-4">
            <span className="text-xs text-gray-500 block mb-1">Seller Notes</span>
            <p className="text-xl text-gray-700">
              {item.note_seller}
            </p>
          </div>

          {/* Actions */}
          <div className="space-y-2">
            <Link
              href={`/sell?brand=${encodeURIComponent(
                item.brand
              )}&model=${encodeURIComponent(item.model)}&category=${encodeURIComponent(
                item.category
              )}&ask=${suggestedAsks[item.sku_id] || item.median_price}`}
              onClick={() =>
                onListClick(item, suggestedAsks[item.sku_id] || item.median_price)
              }
              className="block w-full px-4 py-3 bg-brand-orange text-white text-center font-medium rounded hover:bg-orange-600 transition-colors"
            >
              List this item
            </Link>
            <button
              onClick={() => onCompsClick(item)}
              className="block w-full px-4 py-3 border border-gray-300 text-gray-700 text-center font-medium rounded hover:bg-gray-50 transition-colors"
            >
              See comps
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};
